import { useState } from 'react';
import { Lock, ArrowLeft, CheckCircle, ShieldCheck, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Link } from '../components/Router';

export function ResetPasswordPage() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const inputStyle = "w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:bg-white focus:border-[#D4452C] focus:ring-2 focus:ring-[#D4452C]/20 transition-all outline-none font-medium";

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      setLoading(false);
      return;
    }

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });

      if (updateError) throw updateError;

      setSuccess(true);
      setTimeout(() => {
        window.location.href = '/connecter';
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-24 relative py-8 lg:py-0">
        <div className="absolute top-8 left-8 z-10">
          <Link 
            to="/connecter" 
            className="flex items-center text-sm font-semibold text-gray-500 hover:text-[#D4452C] transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour
          </Link>
        </div>

        <div className="max-w-md w-full mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 tracking-tight mb-2">Nouveau mot de passe</h1>
            <p className="text-gray-500 text-base">Veuillez entrer et confirmer votre nouveau mot de passe.</p>
          </div>

          {success ? (
            <div className="bg-green-50 border border-green-100 rounded-2xl p-6 text-center animate-in fade-in slide-in-from-bottom-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="h-7 w-7 text-green-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">Mot de passe modifié!</h2>
              <p className="text-gray-600 mb-5 text-sm leading-relaxed">
                Votre mot de passe a été réinitialisé avec succès. <br/>
                Vous allez être redirigé vers la page de connexion.
              </p>
              <p className="text-sm font-semibold text-gray-500">
                Redirection en cours...
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-5 p-3 bg-red-50 border border-red-100 rounded-xl flex items-start animate-in fade-in">
                  <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Nouveau mot de passe</label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#D4452C] transition-colors">
                      <Lock className="h-5 w-5" />
                    </div>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={inputStyle}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Confirmer le mot de passe</label>
                  <div className="relative group">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[#D4452C] transition-colors">
                      <Lock className="h-5 w-5" />
                    </div>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={inputStyle}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#D4452C] text-white py-3 rounded-xl font-bold shadow-lg shadow-orange-900/10 hover:bg-[#b83a24] hover:shadow-orange-900/20 active:transform active:scale-[0.99] transition-all disabled:opacity-70 disabled:cursor-not-allowed mt-2"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                      Modification...
                    </div>
                  ) : (
                    'Modifier le mot de passe'
                  )}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-100 text-center">
                <Link to="/connecter" className="text-sm font-semibold text-gray-500 hover:text-[#D4452C] hover:underline">
                  Annuler et se connecter
                </Link>
                
                <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-gray-400">
                  <ShieldCheck className="h-3 w-3" />
                  <span>Réinitialisation sécurisée</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative bg-gray-900">
        <div className="absolute inset-0 bg-gradient-to-bl from-[#D4452C]/30 to-black/70 z-10 mix-blend-multiply" />
        <img 
          src="https://i.ibb.co/jvwCLsJ1/photo2.png" 
          alt="Car Security" 
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
      </div>
    </div>
  );
}